/**
 * Admin V3 — Drivers — Document Verification Modal (Plan §2.2 M-F-11).
 * Wraps POST /admin/drivers/{driver_id}/documents/{doc_id}/verify | reject | request_renewal.
 *
 * Plan §4 Phase 3 acceptance criterion #2: the verify path writes
 * ComplianceReview + ComplianceEvent + admin_audit_log (backend triple-write).
 */
import React, { useState } from 'react';
import { X, Loader2, Check } from 'lucide-react';
import useAdminMutation from '../../shared/hooks/useAdminMutation';
import { t } from '../../i18n';

const TITLE_KEY = {
  verify:          'compliance.verifyModal.title',
  reject:          'compliance.rejectModal.title',
  request_renewal: 'compliance.documents.action.request_renewal',
};

const SUCCESS_KEY = {
  verify:          'compliance.verifyModal.success',
  reject:          'compliance.rejectModal.success',
  request_renewal: 'compliance.verifyModal.success',
};

const DocumentVerificationModal = ({
  driverId, documentId, action,
  onClose, onSuccess, locale = 'fr',
}) => {
  const [notes, setNotes] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState(null);

  const endpoint = `/admin/drivers/${driverId}/documents/${documentId}/${action}`;
  const mut = useAdminMutation(endpoint, 'post');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (action === 'reject' && !reason.trim()) {
      setError(t('compliance.rejectModal.error.reason', locale));
      return;
    }
    const fd = new FormData();
    if (action === 'verify')          fd.append('notes', notes || '');
    if (action === 'reject')          fd.append('reason', reason);
    if (action === 'request_renewal') fd.append('reason', reason || '');

    await mut.mutate(fd, {
      onSuccess: () => { if (onSuccess) onSuccess(); },
      onError:   () => setError(t('compliance.verifyModal.error', locale)),
    });
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center" data-testid="document-verification-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-[18px] font-semibold text-slate-900">{t(TITLE_KEY[action] || TITLE_KEY.verify, locale)}</h3>
          <button type="button" onClick={onClose} className="text-slate-500 hover:text-slate-900">
            <X className="h-5 w-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          {action === 'verify' && (
            <div>
              <label className="block text-sm text-slate-700 mb-1">{t('compliance.verifyModal.field.notes', locale)}</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm"
                data-testid="verify-notes"
              />
            </div>
          )}
          {(action === 'reject' || action === 'request_renewal') && (
            <div>
              <label className="block text-sm text-slate-700 mb-1">
                {action === 'reject' ? t('compliance.rejectModal.field.reason', locale) : t('compliance.approval.field.reason', locale)}
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm"
                data-testid="verify-reason"
              />
            </div>
          )}
          {error && <div className="text-sm text-rose-700">{error}</div>}
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-2 rounded-lg border border-slate-200 text-sm text-slate-700 hover:bg-slate-50"
            >
              {t('compliance.verifyModal.cancel', locale)}
            </button>
            <button
              type="submit"
              disabled={mut.loading}
              className="px-3 py-2 rounded-lg bg-slate-900 text-white text-sm hover:bg-slate-800 inline-flex items-center gap-2 disabled:opacity-50"
              data-testid="verify-submit"
            >
              {mut.loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
              {action === 'verify' ? t('compliance.verifyModal.submit', locale)
               : action === 'reject' ? t('compliance.rejectModal.submit', locale)
               : t('compliance.documents.action.request_renewal', locale)}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DocumentVerificationModal;
